import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../services/api'
import type { LoginCredentials, RegisterData, User } from '../types'

interface UseAuthReturn {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  deleteAccount: () => Promise<void>
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (token) {
      // Try to fetch user info
      apiClient
        .getCurrentUser()
        .then(setUser)
        .catch(() => {
          // Token is invalid or expired, remove it
          localStorage.removeItem('access_token')
          setUser(null)
          // Don't show error message on initial load
        })
        .finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      const token = await apiClient.login(credentials)
      localStorage.setItem('access_token', token.access_token)
      const user = await apiClient.getCurrentUser()
      setUser(user)
    } catch (error: any) {
      // Error is already formatted by apiClient
      throw error instanceof Error ? error : new Error(error?.message || 'Login fehlgeschlagen')
    }
  }, [])

  const register = useCallback(async (data: RegisterData) => {
    try {
      await apiClient.register(data)
      // After registration, automatically log in
      await login({ email: data.email, password: data.password })
    } catch (error: any) {
      // Error is already formatted by apiClient
      throw error instanceof Error ? error : new Error(error?.message || 'Registrierung fehlgeschlagen')
    }
  }, [login])

  const logout = useCallback(() => {
    localStorage.removeItem('access_token')
    setUser(null)
  }, [])

  const deleteAccount = useCallback(async () => {
    try {
      await apiClient.deleteAccount()
      // After account deletion, logout
      logout()
    } catch (error: any) {
      // Error is already formatted by apiClient
      throw error instanceof Error ? error : new Error(error?.message || 'Account-Löschen fehlgeschlagen')
    }
  }, [logout])

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    deleteAccount,
  }
}

